// AppContext — light global state: guidance mode preference, player profile,
// and handoff of a finished session / a take being resumed between screens.

import React, { createContext, useContext, useState, useCallback } from "react";
import { GuidanceMode, PlayerProfile, SessionSummary, JamTake } from "@/models/types";
import { PlayerProfileService } from "@/services/PlayerProfileService";

interface PendingSession {
  summary: SessionSummary;
  variation: number;
}

interface AppContextValue {
  mode: GuidanceMode;
  setMode: (m: GuidanceMode) => void;
  profile: PlayerProfile;
  updateProfile: (p: Partial<PlayerProfile>) => void;
  pendingSession: PendingSession | null;
  setPendingSession: (s: PendingSession | null) => void;
  resumeTake: JamTake | null;
  setResumeTake: (t: JamTake | null) => void;
}

const AppContext = createContext<AppContextValue | null>(null);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const initialProfile = PlayerProfileService.get();
  const [mode, setMode] = useState<GuidanceMode>(initialProfile.preferredGuidance);
  const [profile, setProfile] = useState<PlayerProfile>(initialProfile);
  const [pendingSession, setPendingSession] = useState<PendingSession | null>(null);
  const [resumeTake, setResumeTake] = useState<JamTake | null>(null);

  const updateProfile = useCallback((p: Partial<PlayerProfile>) => {
    setProfile(PlayerProfileService.save(p));
  }, []);

  return (
    <AppContext.Provider
      value={{
        mode,
        setMode,
        profile,
        updateProfile,
        pendingSession,
        setPendingSession,
        resumeTake,
        setResumeTake,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export function useApp(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
}
